import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Unified SEO component with extended metadata & optional Article JSON-LD.
 * Props:
 *  - title, description, url, image
 *  - type: 'website' | 'article'
 *  - publishedTime, modifiedTime (ISO strings)
 *  - author: string
 *  - tags: array of strings
 *  - noindex: boolean
 *  - structuredData: custom object override (if provided, rendered instead of auto Article)
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  tags = [],
  noindex = false,
  structuredData,
  siteName = 'Boxia',
  locale = 'en_US'
}) => {
  const articleLD = type === 'article' && !structuredData ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: image ? [image] : undefined,
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    author: author ? { '@type': 'Person', name: author } : undefined,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: 'https://boxia.io/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://boxia.io/logo192.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    },
    keywords: tags.join(', '),
    inLanguage: locale.replace('_','-')
  } : structuredData;

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {image && <meta property="og:image" content={image} />}
      {url && <meta property="og:url" content={url} />}
      {locale && <meta property="og:locale" content={locale} />}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
      {author && <meta name="author" content={author} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {(modifiedTime || publishedTime) && <meta property="article:modified_time" content={modifiedTime || publishedTime} />}
      {author && <meta property="article:author" content={author} />}
      {tags.map(tag => <meta key={tag} property="article:tag" content={tag} />)}
      {url && <link rel="canonical" href={url} />}
      {articleLD && (
        <script type="application/ld+json">
          {JSON.stringify(articleLD)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;